import React, {useEffect, useState} from "react";
import * as Yup from "yup";
import ValidateMessages from "../../../../../../core/models/static/texts/vallidate-messages";
import {makeRequired, makeValidate} from "mui-rff";
import {DialogActions, DialogContent, Divider, Fade, SvgIcon} from "@mui/material";
import {Col, Row} from "reactstrap";
import Form from "../../../../base/form";
import {OutlinedInputForm} from "../../../../base/input/outlined_input";
import Button from "../../../../base/button/button";
import TryAgain from "../../../../app-specific/try-again";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import Api from "../../../../../../core/services/api/api";
import classnames from "classnames";
import SaveOrRollback from "../../../../app-specific/save-or-rollback";
import IconButton from "../../../../base/button/icon_button";
import {Edit} from "@mui/icons-material";
import {ReactComponent as RemoveIcon} from "../../../../../../assets/images/remove.svg";
import useAccessLevels from "../../../../../hooks/use-access-levels";


const schema = Yup.object().shape({
    newTitle: Yup.string().nullable().required(ValidateMessages.required)
});


const validate = makeValidate(schema);
const required = makeRequired(schema);

const UpsertBypassReasonsSection = ({reasons, setReasons, setInEditMode}) => {
    const [creating, setCreating] = useState(false);
    const isMounted = useIsMounted();
    const {packages} = useAccessLevels();
    const canManageReasons = packages.sales.manageQuoteBypassReasons;

    /**
     * Create a new reason with the given values and adds it to the list of reasons.
     * @param {any} values the form values
     * @param {formApi} form the form
     * @return {Promise<boolean>}
     */
    const createReason = async (values, form) => {
        if (!canManageReasons) return false;
        setCreating(true)
        const forApi = {
            title: values.newTitle,
        }
        const response = await Api.createQuoteBypassReason(forApi);
        if (!isMounted()) return false
        if (response?.resultFlag) {
            setReasons(prevState => [
                ...prevState,
                {
                    ...response.data,
                    editing: false,
                    inEditMode: false,
                    removing: false
                }
            ])
            form.change('newTitle', '')
            form.resetFieldState('newTitle');
        }
        setCreating(false)
        return false
    }

    /**
     * Updates a reason with the given values.
     * @param {any} reason the reason to be updated.
     * @return {Promise<boolean>}
     */
    const updateReason = async (reason) => {
        if (!canManageReasons) return false;
        setReasons(prevState => prevState.map(e => e.id === reason.id ? {
            ...e,
            editing: true
        } : e))
        const forApi = {
            title: reason.title,
            id: reason.id,
        }
        const response = await Api.updateQuoteBypassReason(forApi);
        if (!isMounted()) return false
        setReasons(prevState => prevState.map(e => e.id === reason.id
            ? {
                ...e,
                editing: false,
                ...(response?.resultFlag ? forApi : {})
            }
            : e))
        return response?.resultFlag ?? false
    }

    /**
     * Removes a reason from the server and then removes it from the local list.
     * @param {any} reason the reason to be removed
     */
    const removeReason = async (reason) => {
        if (!canManageReasons) return;
        setReasons(prevState => prevState.map(e => e.id === reason.id ? {
            ...e,
            editing: true
        } : e))
        const response = await Api.removeQuoteBypassReason(reason.id);
        if (!isMounted()) return
        if (response?.resultFlag) {
            setReasons(prevState => prevState.filter(e => e.id !== reason.id))
        } else {
            setReasons(prevState => prevState.map(e => e.id === reason.id ? {
                ...e,
                editing: false
            } : e))
        }
    }


    /**
     * Toggles the edit mode of the reason cards.
     * @param {any} reason
     * @param {boolean} inEditMode
     */
    const toggleReasonEditMode = (reason, inEditMode = false) => {
        setReasons(prevState => prevState.map(e => e.id === reason.id ? {...e, inEditMode} : e))
    }

    return (
        <>
            <DialogContent className={'px-md-4'}>
                <Col xs={12}>
                    <p className={'text-md font-weight-semi-bold mt-4 mb-2'}>
                        Create a new reason
                    </p>
                </Col>
                <Form
                    className='d-flex flex-wrap w-100 align-items-center'
                    onSubmit={createReason}
                    validate={validate}
                    initialValues={{}}
                    render={({values, form}) => {
                        return (
                            <>
                                <Col className={'pl-3'}>
                                    <OutlinedInputForm
                                        name={'newTitle'}
                                        placeholder={'reason name...'}
                                        label={"Name"}
                                        required={required.newTitle}
                                        value={values.newTitle ?? ''}
                                        onChange={(e) => form.change(e.target.name, e.target.value)}
                                    />
                                </Col>
                                <Col xs={'auto'}>
                                    <Button
                                        className='py-2-5 px-5'
                                        type={'submit'}
                                        variant={'contained'}
                                        color={'primary'}
                                        disabled={creating}
                                    >
                                        {
                                            creating
                                                ? "Creating..."
                                                : "Create"
                                        }
                                    </Button>
                                </Col>
                            </>
                        )
                    }}
                />
                <Col xs={12} className={'mt-5 mb-5 d-flex flex-wrap'}>
                    {
                        reasons?.length < 1
                            ? <TryAgain
                                text={"There is currently no reasons submitted. you can add one using" +
                                    " the form above."}
                            />
                            : reasons?.map(reason => (
                                <OrderBypassReasonCard
                                    key={reason.id}
                                    reason={reason}
                                    update={updateReason}
                                    remove={removeReason}
                                    setEditing={(e) => toggleReasonEditMode(reason, e)}
                                />
                            ))

                    }
                </Col>
            </DialogContent>
            <DialogActions className={'p-0'}>
                <Row className={'w-100 no-gutters'}>
                    <Divider className='divider'/>
                    <Row className='no-gutters justify-content-between align-items-start w-100'>
                        <Button
                            disabled={creating}
                            variant={'dialog'}
                            color={'primary'}
                            onClick={() => setInEditMode(false)}
                            borderRadius={'0 8px 0 8px'}
                        >
                            Back
                        </Button>
                    </Row>
                </Row>
            </DialogActions>
        </>
    );
}

const OrderBypassReasonCard = ({reason, update, remove, setEditing}) => {
    const [initialValues, setInitialValues] = useState({});
    const isMounted = useIsMounted()

    const editing = reason?.inEditMode;

    /**
     * Listens to the changes in the category and with each change:
     * sets the initialValues of the form.
     */
    useEffect(() => {
        setInitialValues({
            title: reason?.title ?? '',
        })
    }, [reason])

    /**
     * Prepares the new version of the category to be sent through api and then based on the returned resultFlag,
     * remains in or gets out of the edit mode.
     * @param {mode} values formValues
     */
    const updateReason = (values) => {
        if (values?.title === reason?.title) {
            setEditing(false)
            return
        }
        const newTitle = {
            ...reason,
            title: values.title,
        }
        update(newTitle).then(resultFlag => resultFlag && isMounted() && setEditing(false))
    }

    /**
     * Reverts the values of this template.
     * @param {any} values
     * @param {formApi} form
     */
    const revertValues = (values, form) => {
        setEditing(false)
        form.reset(initialValues)
    }

    /**
     * Gets users confirmation before removing the current entity.
     */
    const getRemoveConfirmation = () => {
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    Do you want to delete this bypass entry?
                </p>
            </div>,
            {
                maxWidth: 'sm'
            },
            (confirmed) => confirmed && remove(reason).then(),
        );
    }

    return (
        <>
            <div className={classnames('order-bypass-reason-card', {'editing': editing})}>
                <Form
                    className={''}
                    onSubmit={() => false}
                    validate={validate}
                    initialValues={initialValues}
                    render={({values, form}) => {
                        return (
                            <>
                                <div className={'d-flex flex-column'}>
                                    <div className={classnames('fade-container', {
                                        'expanded': editing,
                                    })}>
                                        <Fade in={editing}
                                              className={'d-flex justify-content-between align-items-center'}>
                                            <div>
                                                <OutlinedInputForm
                                                    name={'title'}
                                                    placeholder={'title'}
                                                    label={"Title"}
                                                    multiline
                                                    className={'mr-2 max-w-80'}
                                                    required={required.title}
                                                    value={values.title ?? ''}
                                                    onChange={(e) => form.change(e.target.name, e.target.value)}
                                                />
                                                <SaveOrRollback
                                                    column={false}
                                                    loading={reason.editing}
                                                    show={true}
                                                    onRollback={() => revertValues(values, form)}
                                                    onSave={() => updateReason(values)}
                                                />
                                            </div>
                                        </Fade>
                                        <Fade in={!editing}
                                              className={'d-flex align-items-center justify-content-between'}>
                                            <div>
                                                <p className={'title flex-grow-1'}>
                                                    {reason?.title ?? ''}
                                                </p>
                                                <div className={'d-flex justify-content-end align-items-center'}>
                                                    <IconButton
                                                        disabled={reason.editing || editing}
                                                        className={"mx-1 ml-2"}
                                                        color={'paper.secondary'}
                                                        onPressed={() => setEditing(true)}
                                                    >
                                                        <Edit className={"icon normal"}/>
                                                    </IconButton>
                                                    <IconButton
                                                        onPressed={getRemoveConfirmation}
                                                        disabled={reason?.editing}
                                                        color={'paper.error'}
                                                        className={'ml-2'}
                                                    >
                                                        <SvgIcon
                                                            component={RemoveIcon}
                                                            className='icon normal'
                                                        />
                                                    </IconButton>
                                                </div>
                                            </div>
                                        </Fade>
                                    </div>
                                </div>
                            </>
                        );
                    }}
                />
            </div>
        </>
    );
}


export default UpsertBypassReasonsSection;
